/** Call logs list and detail. Callers pass arguments positionally, so the order is fixed. */
import constants from '../../constants'
import { getCallApiBase, getCallApiHeaders } from '../chatApiClient'
import axios from '../axiosInstance'
import {
  ChatApp,
  CallLogsFilters,
  CallLogsListResponse,
  CallDetailResponse,
  NumericFilter,
  NumericFilterOperator,
} from './types'

/** The API silently ignores unknown operators, so this list is the only guard. */
const VALID_OPERATORS: NumericFilterOperator[] = ['gt', 'lt', 'eq', 'gte', 'lte']

function appendNumericFilter(
  params: URLSearchParams,
  key: string,
  filter?: NumericFilter | null,
): void {
  if (filter && VALID_OPERATORS.includes(filter.operator)) {
    params.append(`${key}[${filter.operator}]`, String(filter.value))
  }
}

/** `startRange`/`endRange` are epoch seconds; `0` means "no bound". */
export async function get(
  app: ChatApp,
  callsHost: string,
  type = 'calls',
  startRange?: number,
  endRange?: number,
  filters: CallLogsFilters = {},
  pageNo = 1,
): Promise<CallLogsListResponse> {
  let URL = `${getCallApiBase(app, callsHost)}/${type}`

  const params = new URLSearchParams()
  params.append('perPage', String(constants.perPageInListings))
  params.append('page', String(pageNo))
  if (startRange) params.append('startedAt', String(startRange))
  if (endRange) params.append('endedAt', String(endRange))

  if (filters.mode) params.append('mode', filters.mode)
  if (filters.status) params.append('status', filters.status)
  if (filters.hasRecording) params.append('hasRecording', filters.hasRecording)
  if (filters.receiverType) params.append('receiverType', filters.receiverType)

  appendNumericFilter(params, 'duration', filters.duration)
  appendNumericFilter(params, 'totalParticipants', filters.totalParticipants)
  appendNumericFilter(params, 'voiceMinutes', filters.voiceMinutes)
  appendNumericFilter(params, 'videoMinutes', filters.videoMinutes)

  const queryString = params.toString()
  if (queryString) URL += `?${queryString}`

  return axios
    .get(`${URL}`, { headers: getCallApiHeaders(app) })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

/** `data` is an array even for one session: records are disambiguated by `mid`. */
export async function getByID(
  app: ChatApp,
  callsHost: string,
  sessionID: string,
): Promise<CallDetailResponse | null> {
  const URL = `${getCallApiBase(app, callsHost)}/calls/${sessionID}`
  return axios
    .get(`${URL}`, { headers: getCallApiHeaders(app) })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}
